import StaffVisitOffersFeed from '../components/staff-visit-offers-feed.tsx';
import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import './schedule-pages.css';
import { colorMatches } from '@shared/schedule-colors.ts';
import {
  ColorLegend,
  VisitColorSignals,
  visitColorClass,
  visitColorStyle,
} from '../components/schedule/visit-colors.tsx';
import LiveMapPage, { prefersMap } from './live-map.tsx';
import { api } from '../api/client.ts';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, integrationsApi, scheduleApi, type ExternalCalendarEvent } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, LoadingBlock, classes } from '../components/ui.tsx';
import { useDocumentTitle, useStoredPreference } from '../hooks/index.ts';
import { AppointmentBadge } from '../components/schedule/appointment-badge.tsx';
import { VisitManagePanel } from '../components/schedule/visit-manage-panel.tsx';
import { PaymentBadge } from '../components/payments/payment-badge.tsx';
import { APPOINTMENT_TONES, todayInTimezone, zonedDateTime } from '../components/schedule/format.ts';
import { formatLocalTime, formatShortDate } from '../i18n/datetime.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { addDaysToDate, dayOfWeekOf } from '@shared/calculations/recurrence.ts';
import { APPOINTMENT_PAYMENT_STATUSES, type AppointmentPaymentStatus } from '@shared/constants/index.ts';
import type { Appointment } from '@shared/types/index.ts';
import { isoDateSchema } from '@shared/schemas/common.ts';
import {
  ScheduleViewSelector,
  SCHEDULE_VIEWS,
  type CalendarView,
  type ScheduleView,
} from '../components/schedule/view-selector.tsx';

/**
 * The company calendar: a rolling 30-day list, a week laned by person, or a
 * month grid. All three read the same range endpoint; clicking any visit — a
 * row in the list, a chip in a grid — opens the manage panel for it, with the
 * per-visit actions and the whole-schedule ones together.
 *
 * The list opens first. "What is coming up, and in what order" is the question
 * the page is opened with, and the list answers it in reading order — with the
 * time, the customer and the address on every row, none of which fit in a grid
 * cell. The month grid is a click away for the shape of a whole month, and
 * whichever was chosen last is what opens next time: someone who works from the
 * month should not have to say so again every morning.
 *
 * In the list the panel opens under the row that was clicked rather than at
 * the foot of the page: the row is the thing being answered about, and a panel
 * that appears somewhere else reads as a separate part of the page instead of
 * as the row opening up.
 */
/**
 * Whether the month and week grids fit.
 *
 * Below `md` they become an agenda: a seven-column grid is wider than a
 * phone, and clipping Thursday off the edge is not a calendar. Missing
 * `matchMedia` (jsdom) is treated as wide so the grid the tests read is the
 * one that renders — a phone always has `matchMedia`.
 */
function useWideCalendar(): boolean {
  const query = '(min-width: 768px)';
  const [wide, setWide] = useState(() => window.matchMedia?.(query)?.matches ?? true);
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return;
    setWide(list.matches);
    const onChange = (event: MediaQueryListEvent) => setWide(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);
  return wide;
}

/** Every crew member's name the team list can vouch for, joined for display. */
function crewNames(assignedUserIds: string[], teamById: Map<string, string>): string | null {
  const names = assignedUserIds.map((userId) => teamById.get(userId)).filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(', ') : null;
}

/**
 * The plan decides whether this page exists for a company at all. The check
 * is a wrapper rather than an early return inside the page, so the body's
 * hooks keep running in the same order on every render.
 */
export default function SchedulePage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const { company, user, currentLocation } = useSession();
  const scheduling = usePlanFeature('scheduling');
  // Map is a destination, never a remembered default. Legacy map preferences
  // are deliberately ignored; the existing calendar preference remains valid.
  const [savedView, saveView] = useStoredPreference('schedule-view', SCHEDULE_VIEWS, 'list');
  const requestedView = params.get('view');
  const view = SCHEDULE_VIEWS.find((option) => option === requestedView) ?? savedView;
  function selectView(nextView: ScheduleView, base?: URLSearchParams) {
    const next = new URLSearchParams(base ?? params);
    if (!base) next.delete('date');
    next.set('view', nextView);
    if (nextView !== 'map') saveView(nextView);
    if (next.toString() !== params.toString()) {
      // Freeze an unqualified arrival before adding the first choice to
      // history, so Back restores its view even after the saved view changes.
      if (!SCHEDULE_VIEWS.some((option) => option === requestedView) && requestedView !== 'map' && nextView !== view) {
        const previous = new URLSearchParams(params);
        previous.set('view', view);
        setParams(previous, { replace: true });
      }
      setParams(next);
    }
  }
  const capability = useQuery({
    queryKey: ['live-map-capabilities', company?.id, user?.id, currentLocation?.id],
    queryFn: ({ signal }) => api.getFresh<{ enabled: boolean; dayMap: boolean }>('/schedule/map-capabilities', signal),
    enabled: scheduling,
    refetchInterval: 15000,
    retry: false,
  });
  if (!scheduling) return <PlanRequiredPage feature="scheduling" title={t('schedule.page.title')} />;
  const mapAvailable = Boolean(capability.data?.enabled && capability.data.dayMap && !capability.error);
  if (prefersMap(params) && mapAvailable)
    return (
      <LiveMapPage
        scheduleSelector={(disabled) => (
          <ScheduleViewSelector view="map" mapAvailable onSelect={selectView} disabled={disabled} />
        )}
      />
    );
  return (
    <>
      <div className="mb-3 flex flex-wrap gap-2">
        <Link to="/today" className="btn btn-primary">
          {t('todayRoute.title')}
        </Link>
        <Link to="/schedule/availability" className="btn btn-secondary">
          {t('planning.title')}
        </Link>
      </div>
      <SchedulePageContent view={view} selectView={selectView} mapAvailable={mapAvailable} />
    </>
  );
}

function SchedulePageContent({
  view,
  selectView,
  mapAvailable,
}: {
  view: CalendarView;
  selectView: (view: ScheduleView, base?: URLSearchParams) => void;
  mapAvailable: boolean;
}) {
  const { t } = useTranslation();
  useDocumentTitle(t('schedule.page.documentTitle'));
  const wideCalendar = useWideCalendar();
  const { can, company, currentLocation, locations, switchLocation } = useSession();

  const WEEKDAY_HEADERS = [
    t('schedule.page.weekdaySun'),
    t('schedule.page.weekdayMon'),
    t('schedule.page.weekdayTue'),
    t('schedule.page.weekdayWed'),
    t('schedule.page.weekdayThu'),
    t('schedule.page.weekdayFri'),
    t('schedule.page.weekdaySat'),
  ];

  const today = todayInTimezone(currentLocation?.timezone ?? company?.timezone);
  const [monthStart, setMonthStart] = useState(() => `${today.slice(0, 7)}-01`);
  const [weekStart, setWeekStart] = useState(today);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // '' = everyone, 'unassigned' = nobody's visits, otherwise a team member id.
  const [assignee, setAssignee] = useState('');
  const [colorFilter, setColorFilter] = useState('');

  /**
   * The dashboard's "needs attention" card links here with `?paymentStatus=`
   * — the failed-payments row above all. It replaces the calendar's usual
   * views with a flat list, since the visit it is pointing at may well be
   * behind today: a card declined on completion is a PAST visit, and the
   * list view's normal window only looks ahead.
   *
   * A "visit still open" notification (and the calendar invite's Open link)
   * lands here with `?appointment=` — and optionally `&complete=1` so the
   * close-out form is already open. Those visits are often behind today for
   * the same reason, so they share the list-only path and are fetched by id
   * when the rolling window does not hold them.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const planningDate = isoDateSchema.safeParse(searchParams.get('date')).data;
  const [teamFlexibleOnly, setTeamFlexibleOnly] = useState(false);
  const paymentStatusParam = searchParams.get('paymentStatus');
  const paymentStatusFilter: AppointmentPaymentStatus | null =
    paymentStatusParam && (APPOINTMENT_PAYMENT_STATUSES as readonly string[]).includes(paymentStatusParam)
      ? (paymentStatusParam as AppointmentPaymentStatus)
      : null;
  const calendarFiltered = Boolean(assignee || teamFlexibleOnly || colorFilter);

  function clearFilters() {
    setAssignee('');
    setTeamFlexibleOnly(false);
    setColorFilter('');
    if (paymentStatusFilter) {
      const next = new URLSearchParams(searchParams);
      next.delete('paymentStatus');
      setSearchParams(next, { replace: true });
    }
  }
  const deepLinkAppointmentId = searchParams.get('appointment');
  const startCompletingFromLink = searchParams.get('complete') === '1';
  // Matches the dashboard's own trailing-30-day window for the same count
  // (see loadDashboardData), widened a month ahead too — a visit already on
  // the books with a failed payment (a scheduled deposit, say) belongs here
  // just as much as one already completed.
  const effectiveView = paymentStatusFilter || deepLinkAppointmentId || planningDate ? 'list' : view;
  // The dashboard's count can be an "all locations" aggregate (see its scope
  // toggle), but this page — like every other schedule view — only ever
  // reads the session's current working location; there is no cross-location
  // calendar to send it to. `scope=all` on arrival says the count behind
  // this link may include other locations, so the list below says so too
  // rather than quietly looking shorter than the count it came from.
  const fromAllLocationsScope = searchParams.get('scope') === 'all' && locations.length > 1;

  useEffect(() => {
    if (deepLinkAppointmentId) setSelectedId(deepLinkAppointmentId);
  }, [deepLinkAppointmentId]);

  /**
   * Put the session on the visit's branch before asking for the visit.
   *
   * Every read on this page is scoped to the session's current location, and
   * `GET /schedule/appointments/:id` refuses one belonging to another branch
   * outright (`assertSameLocation`). So a link followed by somebody whose
   * session happens to sit on a different branch — the ordinary case for an
   * owner who covers two — opened a panel with no visit in it and no
   * explanation. The alerts that carry `?appointment=` now carry the branch
   * too.
   *
   * Only to a branch this person already has: `locations` is their own list.
   * A digest date waits for that branch, including on a failed switch, rather
   * than displaying the same date from the session's fallback branch.
   */
  const deepLinkLocationId = searchParams.get('location');
  const deepLinkLocationAvailable = locations.some((entry) => entry.id === deepLinkLocationId);
  const digestBranchReady =
    !planningDate || !deepLinkLocationId || (deepLinkLocationAvailable && deepLinkLocationId === currentLocation?.id);
  const [locationSwitchFailure, setLocationSwitchFailure] = useState<string | null>(null);
  const [locationSwitchAttempt, setLocationSwitchAttempt] = useState(0);
  useEffect(() => {
    if (!deepLinkLocationId || deepLinkLocationId === currentLocation?.id) return;
    if (!deepLinkLocationAvailable) return;
    let active = true;
    setLocationSwitchFailure(null);
    void switchLocation(deepLinkLocationId).catch(() => {
      if (active) setLocationSwitchFailure(deepLinkLocationId);
    });
    return () => {
      active = false;
    };
  }, [deepLinkLocationId, deepLinkLocationAvailable, currentLocation?.id, switchLocation, locationSwitchAttempt]);

  /** Drop the deep-link query once the visit is no longer the one on screen. */
  function clearedScheduleDeepLink(includePlanningDate = false) {
    const next = new URLSearchParams(searchParams);
    if (!deepLinkAppointmentId && !startCompletingFromLink && !(includePlanningDate && planningDate)) return next;
    next.delete('appointment');
    next.delete('complete');
    // "Add extra work" from a customer's upcoming visits; for that visit only.
    next.delete('extraWork');
    if (includePlanningDate) {
      next.delete('date');
      next.delete('view');
    }
    // The branch travelled with the visit and has nothing left to say once
    // the visit is off screen — the session is already on it, and leaving it
    // in the URL would switch back on every refresh.
    next.delete('location');
    return next;
  }

  function clearScheduleDeepLink(includePlanningDate = false) {
    const next = clearedScheduleDeepLink(includePlanningDate);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }

  function setView(option: ScheduleView) {
    if (deepLinkAppointmentId) setSelectedId(null);
    const next = clearedScheduleDeepLink(true);
    if (option === 'map') next.delete('location');
    selectView(option, next);
  }

  function selectAppointment(id: string | null) {
    setSelectedId(id);
    if (id !== deepLinkAppointmentId) clearScheduleDeepLink();
  }

  const team = useQuery({ queryKey: ['team'], queryFn: () => companyApi.users() });
  const teamById = useMemo(
    () => new Map((team.data?.items ?? []).map((member) => [member.id, member.fullName])),
    [team.data],
  );
  const ASSIGNEE_OPTIONS = [
    { value: '', label: t('schedule.page.assigneeEveryone') },
    { value: 'unassigned', label: t('schedule.page.assigneeUnassigned') },
    ...(team.data?.items ?? [])
      .filter((member) => member.isActive)
      .map((member) => ({ value: member.id, label: member.fullName })),
  ];

  // The month grid always renders 6 whole weeks, padded with the neighboring
  // months, so the range fetched is the range drawn.
  const gridStart = useMemo(() => addDaysToDate(monthStart, -dayOfWeekOf(monthStart)), [monthStart]);
  // Keyed to the view actually being DRAWN, not the stored preference: a
  // notification or calendar link forces the list (see `effectiveView`), and
  // a list fetched over the month grid's range is a list of the padded six
  // weeks around the current month — previous-month days and past days
  // included — under a heading that promises the next 30 days.
  const from = paymentStatusFilter
    ? addDaysToDate(today, -30)
    : planningDate
      ? planningDate
      : effectiveView === 'month'
        ? gridStart
        : effectiveView === 'week'
          ? weekStart
          : today;
  const to = paymentStatusFilter
    ? addDaysToDate(today, 30)
    : planningDate
      ? planningDate
      : effectiveView === 'month'
        ? addDaysToDate(gridStart, 41)
        : effectiveView === 'week'
          ? addDaysToDate(weekStart, 6)
          : addDaysToDate(today, 30);

  const appointments = useQuery({
    enabled: digestBranchReady,
    queryKey: ['appointments', from, to, assignee, paymentStatusFilter, teamFlexibleOnly, currentLocation?.id],
    queryFn: () =>
      scheduleApi.appointments({
        from,
        to,
        ...(assignee ? { assignedUserId: assignee } : {}),
        ...(teamFlexibleOnly ? { teamFlexibleOnly: true } : {}),
        ...(paymentStatusFilter ? { paymentStatus: paymentStatusFilter } : {}),
      }),
  });

  /**
   * The visit named in the URL, fetched on its own when the rolling window
   * does not hold it — overdue close-outs sit behind today, and the list's
   * usual from-date is today.
   */
  const deepLinkedAppointment = useQuery({
    queryKey: ['appointment', deepLinkAppointmentId, currentLocation?.id],
    queryFn: () => scheduleApi.appointment(deepLinkAppointmentId!),
    enabled: digestBranchReady && Boolean(deepLinkAppointmentId),
  });

  /**
   * Range answer plus the deep-linked visit when it would otherwise be
   * missing — kept in the list so the manage panel has a row to open under,
   * the same way the messages inbox holds a thread that the current filter
   * no longer returns.
   */
  const allAppointmentItems = useMemo(() => {
    const items = appointments.data?.items ?? [];
    const extra = deepLinkedAppointment.data;
    if (!extra || items.some((appointment) => appointment.id === extra.id)) return items;
    return [extra, ...items];
  }, [appointments.data, deepLinkedAppointment.data]);

  const appointmentItems = useMemo(
    () => allAppointmentItems.filter((v) => colorMatches(v.colors, colorFilter)),
    [allAppointmentItems, colorFilter],
  );

  // Server-side ceiling reached: the grid below is missing visits. Said out
  // loud, because an incomplete calendar looks exactly like a quiet one.
  const truncated = appointments.data?.truncated ?? false;

  /**
   * What else is on the calendar of whoever is being shown.
   *
   * Only for the calendars that person set to display — availability and
   * display are separate switches, and a calendar that blocks bookings without
   * showing anything is the default. So an empty answer here means "nobody
   * chose to show anything", never "everybody is free".
   *
   * Asked for one person at a time. With "Everyone" selected the server answers
   * for the signed-in user's own calendars, which is the only set that is
   * theirs to see without asking for it; "Unassigned" has no person at all, so
   * nothing is fetched.
   *
   * The window is widened a day either side of the drawn range because these
   * are instants and the grid is in company-local days — an event late on the
   * last evening belongs to a day the range does not contain in UTC.
   */
  const zone = currentLocation?.timezone ?? company?.timezone ?? null;
  const externalPersonId = assignee === 'unassigned' ? null : assignee || 'me';
  const externalEvents = useQuery({
    // The payment-status filter's own list never merges these in (see
    // listRows below) — fetching a 60-day-wide window from a rate-limited
    // third-party calendar just to throw the answer away would be a real
    // cost for a page whose whole point is to be a quick, minimal worklist.
    enabled: digestBranchReady && externalPersonId !== null && !paymentStatusFilter,
    queryKey: ['external-calendar-events', from, to, externalPersonId, currentLocation?.id],
    queryFn: () =>
      integrationsApi.calendarEvents({
        startsAt: `${addDaysToDate(from, -1)}T00:00:00.000Z`,
        endsAt: `${addDaysToDate(to, 2)}T00:00:00.000Z`,
        ...(externalPersonId && externalPersonId !== 'me' ? { userIds: [externalPersonId] } : {}),
      }),
  });

  /** The events, placed on the company-local day each one actually falls on. */
  const externalByDate = useMemo(() => {
    const map = new Map<string, Array<ExternalCalendarEvent & { localTime: string }>>();
    for (const event of externalEvents.data?.items ?? []) {
      // Ours already, drawn as the visit it is. Showing both would double every
      // job on the calendar of the person doing it.
      if (event.appointmentId) continue;
      const { date, time } = zonedDateTime(event.startsAt, zone);
      const existing = map.get(date) ?? [];
      existing.push({ ...event, localTime: time });
      map.set(date, existing);
    }
    for (const entries of map.values()) entries.sort((left, right) => left.localTime.localeCompare(right.localTime));
    return map;
  }, [externalEvents.data, zone]);

  /** "Busy" where the calendar keeps its detail private; the title otherwise. */
  const externalLabel = (event: ExternalCalendarEvent) => event.title ?? t('schedule.page.externalBusy');

  /**
   * The list, with the external events threaded through it in time order.
   *
   * Interleaved rather than kept in a section of their own: the list answers
   * "what is coming up, in what order", and an hour that is already spoken for
   * is part of that answer wherever it falls.
   *
   * Skipped entirely under the payment-status filter: that view promises a
   * short worklist of visits with a failed payment, and a personal calendar's
   * busy blocks are neither that nor clickable toward fixing one — they would
   * only pad out the 60-day window with rows the banner didn't say to expect.
   */
  const listRows = useMemo(() => {
    const rows: Array<
      | { kind: 'visit'; key: string; sort: string; appointment: Appointment }
      | {
          kind: 'external';
          key: string;
          sort: string;
          date: string;
          event: ExternalCalendarEvent & { localTime: string };
        }
    > = appointmentItems.map((appointment) => ({
      kind: 'visit',
      key: appointment.id,
      sort: `${appointment.scheduledDate}T${appointment.startTimeLocal}`,
      appointment,
    }));

    if (paymentStatusFilter) return rows.sort((left, right) => left.sort.localeCompare(right.sort));

    for (const [date, events] of externalByDate) {
      // Back to the range actually being drawn: the fetch was widened by a day
      // either side so no local day could be clipped by the UTC boundary.
      if (date < from || date > to) continue;
      for (const event of events) {
        rows.push({ kind: 'external', key: event.id, sort: `${date}T${event.localTime}`, date, event });
      }
    }

    return rows.sort((left, right) => left.sort.localeCompare(right.sort));
  }, [appointmentItems, externalByDate, from, to, paymentStatusFilter]);

  const byDate = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const appointment of appointmentItems) {
      const existing = map.get(appointment.scheduledDate) ?? [];
      existing.push(appointment);
      map.set(appointment.scheduledDate, existing);
    }
    return map;
  }, [appointmentItems]);

  /**
   * Visits per (person, day) for the week lanes. Crews are ad-hoc per job, so
   * the stable rows are PEOPLE: a two-cleaner visit simply appears in both
   * lanes — that is the joint commitment the view exists to show — and work
   * with nobody named sits in the Unassigned lane it blocks everyone from.
   */
  const byLane = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const appointment of appointmentItems) {
      const lanes = appointment.assignedUserIds.length > 0 ? appointment.assignedUserIds : ['unassigned'];
      for (const lane of lanes) {
        const key = `${lane}|${appointment.scheduledDate}`;
        const existing = map.get(key) ?? [];
        existing.push(appointment);
        map.set(key, existing);
      }
    }
    return map;
  }, [appointmentItems]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => addDaysToDate(weekStart, index)), [weekStart]);
  // The days that belong to the month being shown, without the grid's
  // padding from the months on either side. The phone agenda lists these;
  // the six-week grid stays for wider screens.
  const monthDays = useMemo(() => {
    const [year, month] = monthStart.split('-').map(Number) as [number, number];
    const count = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return Array.from(
      { length: count },
      (_, index) => `${monthStart.slice(0, 8)}${String(index + 1).padStart(2, '0')}`,
    );
  }, [monthStart]);
  // The lanes follow the assignee filter: one person filtered = one lane.
  // Deactivated members keep their lane while visits still name them — a
  // visit must not vanish from the week because its cleaner left.
  const referenced = new Set(appointmentItems.flatMap((visit) => visit.assignedUserIds));
  const laneMembers = (team.data?.items ?? [])
    .filter((member) => member.isActive || referenced.has(member.id))
    .filter((member) => !assignee || assignee === member.id);
  const showUnassignedLane = !assignee || assignee === 'unassigned';

  const selected = appointmentItems.find((appointment) => appointment.id === selectedId) ?? null;

  /**
   * Ctrl/⌘-click or middle-click on a visit opens its customer in a new tab,
   * the way it would on a link, in every view. A plain click still opens the
   * manage panel, whose customer name is the visible way to the same tab.
   * Returns whether it opened one, so a click that did skips the panel.
   */
  const canReadCustomers = can('customers:read');
  function openCustomerTab(event: MouseEvent, appointment: Appointment): boolean {
    if (!canReadCustomers) return false;
    if (event.button !== 1 && !event.metaKey && !event.ctrlKey) return false;
    event.preventDefault();
    window.open(`/customers/${appointment.customerId}`, '_blank', 'noopener');
    return true;
  }

  /** The clickable visit chip both grids share; opening remembers the opener. */
  const visitChip = (appointment: Appointment) => (
    <button
      key={appointment.id}
      style={visitColorStyle(appointment.colors)}
      type="button"
      onClick={(event) => {
        if (openCustomerTab(event, appointment)) return;
        const opening = appointment.id !== selectedId;
        openerRef.current = opening ? event.currentTarget : null;
        selectAppointment(opening ? appointment.id : null);
      }}
      onAuxClick={(event) => openCustomerTab(event, appointment)}
      aria-pressed={appointment.id === selectedId}
      className={classes(
        'mb-1 block w-full cursor-pointer truncate rounded border px-1 py-0.5 text-left text-xs',
        'hover:border-[color:var(--color-brand)] hover:bg-[color:var(--color-brand-soft)]',
        APPOINTMENT_TONES[appointment.status],
        visitColorClass(appointment.colors),
        appointment.id === selectedId && 'ring-2 ring-[color:var(--color-brand)]',
      )}
      title={t('schedule.page.manageVisitTitle', {
        time: formatLocalTime(appointment.startTimeLocal),
        customer: appointment.customerName,
      })}
    >
      {formatLocalTime(appointment.startTimeLocal)} {appointment.customerName}
      <VisitColorSignals colors={appointment.colors} />
    </button>
  );

  /**
   * Opening a visit in the month grid moves the page to it, and closing moves
   * it back.
   *
   * The grid is six weeks tall, so on a phone — and on a laptop in the second
   * half of the month — the panel opens below the fold, and a click that
   * appears to do nothing is a click nobody makes twice. Scrolling is paired
   * with focus so the same thing happens for someone on a keyboard or a screen
   * reader, who would otherwise be left at the top of a page whose bottom just
   * changed. `preventScroll` keeps focus from doing its own instant jump and
   * fighting the smooth one; a reduced-motion preference gets the jump instead.
   */
  const monthPanelRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (effectiveView === 'list') return;

    if (!selectedId) {
      // Closed from inside the panel: back to the chip it came from, which is
      // where the calendar the visit belongs to is.
      openerRef.current?.focus();
      openerRef.current = null;
      return;
    }

    const panel = monthPanelRef.current;
    if (!panel) return;
    panel.focus({ preventScroll: true });
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    panel.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, [selectedId, effectiveView]);

  // Month and year only. A short/medium/long date override must not turn this
  // heading into a day (`11/1/26`). The company locale still names the month.
  const monthLabel = new Intl.DateTimeFormat(getDateLocale(), {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${monthStart}T12:00:00Z`));

  function shiftMonth(byMonths: number) {
    const [year, month] = monthStart.split('-').map(Number) as [number, number];
    const next = new Date(Date.UTC(year, month - 1 + byMonths, 1));
    setMonthStart(next.toISOString().slice(0, 10));
  }

  /**
   * The month and week grids, as a vertical list.
   *
   * A seven-column grid is wider than a phone. Hiding empty days in the month
   * keeps a quiet month to the days that have something on them; the week
   * keeps every day so the seven-day shape is still readable.
   */
  function renderEmptySchedule() {
    const filtered = Boolean(paymentStatusFilter || calendarFiltered);
    return (
      <WorkEmpty title={t(filtered ? 'schedule.page.filteredEmptyTitle' : 'schedule.page.emptyTitle')}>
        <p>{t(filtered ? 'schedule.page.filteredEmptyMessage' : 'schedule.page.emptyMessage')}</p>
        {!filtered && can('schedule:write') ? (
          <Link className="btn btn-primary mt-3" to="/schedule/new">
            {t('schedule.page.newSchedule')}
          </Link>
        ) : null}
      </WorkEmpty>
    );
  }

  function renderAgenda(days: string[], hideEmpty: boolean, showCrew: boolean) {
    const visible = hideEmpty
      ? days.filter((date) => (byDate.get(date)?.length ?? 0) > 0 || (externalByDate.get(date)?.length ?? 0) > 0)
      : days;
    if (visible.length === 0) {
      return renderEmptySchedule();
    }
    return (
      <ol className="divide-y divide-[color:var(--color-line)] rounded-lg border border-[color:var(--color-line)] bg-white">
        {visible.map((date) => {
          const visits = byDate.get(date) ?? [];
          const events = externalByDate.get(date) ?? [];
          const entries = [
            ...events.map((event) => ({ kind: 'event' as const, sort: event.localTime, event })),
            ...visits.map((appointment) => ({
              kind: 'visit' as const,
              sort: appointment.startTimeLocal,
              appointment,
            })),
          ].sort((left, right) => {
            const byTime = left.sort.localeCompare(right.sort);
            if (byTime !== 0) return byTime;
            if (left.kind === right.kind) return 0;
            return left.kind === 'visit' ? -1 : 1;
          });
          return (
            <li key={date} className="px-3 py-3">
              <p className={date === today ? 'font-semibold text-[color:var(--color-brand)]' : 'font-semibold'}>
                {formatShortDate(date)}
              </p>
              {entries.map((entry) =>
                entry.kind === 'event' ? (
                  <p key={entry.event.id} className="mt-2 text-sm text-[color:var(--color-muted)]">
                    {formatLocalTime(entry.event.localTime)} {externalLabel(entry.event)}
                  </p>
                ) : (
                  <button
                    key={entry.appointment.id}
                    style={visitColorStyle(entry.appointment.colors)}
                    type="button"
                    className={classes(
                      'mt-2 flex w-full flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left',
                      APPOINTMENT_TONES[entry.appointment.status],
                      visitColorClass(entry.appointment.colors),
                      entry.appointment.id === selectedId && 'ring-2 ring-[color:var(--color-brand)]',
                    )}
                    aria-pressed={entry.appointment.id === selectedId}
                    onAuxClick={(event) => openCustomerTab(event, entry.appointment)}
                    onClick={(event) => {
                      if (openCustomerTab(event, entry.appointment)) return;
                      const opening = entry.appointment.id !== selectedId;
                      openerRef.current = opening ? event.currentTarget : null;
                      selectAppointment(opening ? entry.appointment.id : null);
                    }}
                  >
                    <span className="font-medium">
                      {formatLocalTime(entry.appointment.startTimeLocal)} {entry.appointment.customerName}
                    </span>
                    <VisitColorSignals colors={entry.appointment.colors} />
                    {showCrew ? (
                      <span className="text-sm text-[color:var(--color-muted)]">
                        {crewNames(entry.appointment.assignedUserIds, teamById) ??
                          t('schedule.page.assigneeUnassigned')}
                      </span>
                    ) : null}
                    {entry.appointment.propertyAddress ? (
                      <span className="text-sm text-[color:var(--color-muted)]">
                        {entry.appointment.propertyAddress}
                      </span>
                    ) : null}
                  </button>
                ),
              )}
              {visits.length === 0 && events.length === 0 ? (
                <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('schedule.page.emptyDayMessage')}</p>
              ) : null}
            </li>
          );
        })}
      </ol>
    );
  }

  if (!digestBranchReady) {
    const unavailable = !deepLinkLocationAvailable || locationSwitchFailure === deepLinkLocationId;
    return (
      <WorkPage title={t('schedule.page.title')} description={t('schedule.page.description')}>
        {unavailable ? (
          <Alert tone="error" title={t('schedule.page.locationUnavailableTitle')}>
            <p>{t('schedule.page.locationUnavailableMessage')}</p>
            {deepLinkLocationAvailable && (
              <Button className="mt-3" onClick={() => setLocationSwitchAttempt((attempt) => attempt + 1)}>
                {t('common.retry')}
              </Button>
            )}
          </Alert>
        ) : (
          <LoadingBlock label={t('schedule.page.switchingLocation')} />
        )}
        <Link to="/schedule" className="btn btn-secondary mt-3">
          {t('schedule.page.openCurrentLocation')}
        </Link>
      </WorkPage>
    );
  }

  return (
    <WorkPage
      title={t('schedule.page.title')}
      description={t('schedule.page.description')}
      actions={
        can('schedule:write') ? (
          <Link to="/schedule/new" className="btn btn-primary">
            {t('schedule.page.newSchedule')}
          </Link>
        ) : undefined
      }
    >
      {paymentStatusFilter ? (
        <div className="mb-4">
          <Alert tone="warning" title={t('schedule.page.paymentFilterTitle')}>
            <p className="mb-2">{t('schedule.page.paymentFilterMessage')}</p>
            {fromAllLocationsScope ? (
              <p className="mb-2">
                {t('schedule.page.paymentFilterLocationNote', {
                  location: currentLocation?.name ?? t('schedule.page.paymentFilterLocationFallback'),
                })}
              </p>
            ) : null}
            <Button onClick={clearFilters}>{t('schedule.page.clearFilter')}</Button>
          </Alert>
        </div>
      ) : (
        <div className="card schedule-toolbar mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end">
            <ScheduleViewSelector view={effectiveView} onSelect={setView} mapAvailable={mapAvailable} />
            <label className="flex min-w-0 items-center gap-2 text-sm">
              <span className="font-medium">{t('schedule.page.assigneeLabel')}</span>
              <select
                className="field-control !mb-0 w-full sm:w-auto"
                value={assignee}
                onChange={(event) => setAssignee(event.target.value)}
              >
                {ASSIGNEE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={teamFlexibleOnly}
                onChange={(event) => setTeamFlexibleOnly(event.target.checked)}
              />
              {t('savedTeams.flexible')}
            </label>
            {calendarFiltered ? (
              <Button variant="ghost" onClick={clearFilters}>
                {t('schedule.page.clearFilter')}
              </Button>
            ) : null}
          </div>
          {effectiveView === 'month' ? (
            <div className="flex max-w-full flex-wrap items-center gap-2">
              <Button variant="ghost" onClick={() => shiftMonth(-1)} aria-label={t('schedule.page.previousMonth')}>
                ←
              </Button>
              <span className="min-w-0 flex-1 text-center font-semibold sm:min-w-40 sm:flex-none">{monthLabel}</span>
              <Button variant="ghost" onClick={() => shiftMonth(1)} aria-label={t('schedule.page.nextMonth')}>
                →
              </Button>
              <Button variant="ghost" onClick={() => setMonthStart(`${today.slice(0, 7)}-01`)}>
                {t('schedule.page.today')}
              </Button>
            </div>
          ) : effectiveView === 'week' ? (
            <div className="flex max-w-full flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => setWeekStart(addDaysToDate(weekStart, -7))}
                aria-label={t('schedule.page.previousWeek')}
              >
                ←
              </Button>
              <span className="min-w-0 flex-1 text-center font-semibold sm:min-w-40 sm:flex-none">
                {formatShortDate(weekStart)} – {formatShortDate(addDaysToDate(weekStart, 6))}
              </span>
              <Button
                variant="ghost"
                onClick={() => setWeekStart(addDaysToDate(weekStart, 7))}
                aria-label={t('schedule.page.nextWeek')}
              >
                →
              </Button>
              <Button variant="ghost" onClick={() => setWeekStart(today)}>
                {t('schedule.page.today')}
              </Button>
            </div>
          ) : (
            <span className="text-[color:var(--color-muted)]">
              {planningDate ? (
                <>
                  {formatShortDate(planningDate)}{' '}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setView('list');
                    }}
                  >
                    {t('schedule.page.next30Days')}
                  </Button>
                </>
              ) : (
                t('schedule.page.next30Days')
              )}
            </span>
          )}
        </div>
      )}

      {/* The calendar's one interaction, said rather than implied. */}
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {t('schedule.page.clickHint')}
        {canReadCustomers ? ` ${t('schedule.page.customerTabHint')}` : ''}
      </p>

      {appointments.isLoading || (deepLinkAppointmentId && deepLinkedAppointment.isLoading) ? (
        <LoadingBlock label={t('schedule.page.loading')} />
      ) : null}
      <ColorLegend items={allAppointmentItems} value={colorFilter} onChange={setColorFilter} />
      {appointments.isError ? (
        <Alert tone="error" title={t('schedule.page.loadErrorTitle')}>
          <p>{(appointments.error as Error | null)?.message ?? t('schedule.page.loadErrorFallback')}</p>
          <Button className="mt-3" busy={appointments.isFetching} onClick={() => void appointments.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}

      {truncated ? (
        <div className="mb-4">
          <Alert tone="warning" title={t('schedule.page.truncatedTitle')}>
            <p>{t('schedule.page.truncatedMessage')}</p>
          </Alert>
        </div>
      ) : null}

      {!appointments.isError &&
      appointments.data &&
      effectiveView !== 'list' &&
      listRows.length === 0 &&
      (wideCalendar || effectiveView === 'week')
        ? renderEmptySchedule()
        : null}

      {!appointments.isError && appointments.data && effectiveView === 'month' ? (
        <>
          {/* A 7-column month does not fit a phone: Thursday was clipped off
              the screen. Below `md` the same days are an agenda. The grid
              stays on wider screens, inside its own scroller. */}
          {wideCalendar ? null : renderAgenda(monthDays, true, false)}
          {wideCalendar ? (
            <div className="schedule-calendar overflow-x-auto">
              <table
                className="w-full min-w-[42rem] table-fixed border-collapse"
                aria-label={t('schedule.page.calendarAriaLabel', { month: monthLabel })}
              >
                <thead>
                  <tr>
                    {WEEKDAY_HEADERS.map((day) => (
                      <th
                        key={day}
                        scope="col"
                        className="pb-2 text-left text-sm font-semibold text-[color:var(--color-muted)]"
                      >
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 6 }, (_, week) => (
                    <tr key={week}>
                      {Array.from({ length: 7 }, (_, weekday) => {
                        const date = addDaysToDate(gridStart, week * 7 + weekday);
                        const inMonth = date.slice(0, 7) === monthStart.slice(0, 7);
                        const visits = byDate.get(date) ?? [];
                        return (
                          <td
                            key={date}
                            className={classes(
                              'h-24 min-w-24 border border-[color:var(--color-line)] p-1 align-top',
                              !inMonth && 'bg-[color:var(--color-surface)] opacity-60',
                            )}
                          >
                            <p
                              className={classes(
                                'mb-1 text-right text-sm',
                                date === today
                                  ? 'font-bold text-[color:var(--color-brand)]'
                                  : 'text-[color:var(--color-muted)]',
                              )}
                            >
                              {Number(date.slice(8, 10))}
                            </p>
                            {(externalByDate.get(date) ?? []).map((event) => (
                              // Not a button: there is nothing to manage. It is
                              // somebody's own commitment, shown so the office can
                              // see why a morning is not as free as it looks.
                              <span
                                key={event.id}
                                className="mb-1 block w-full truncate rounded border border-dashed border-[color:var(--color-line)] px-1 py-0.5 text-left text-xs text-[color:var(--color-muted)]"
                                title={t('schedule.page.externalEventTitle', { label: externalLabel(event) })}
                              >
                                {formatLocalTime(event.localTime)} {externalLabel(event)}
                              </span>
                            ))}
                            {visits.map(visitChip)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {/* The week, by person. Crews are ad-hoc per job, so the stable unit is
          the person: a two-cleaner visit shows in both lanes, and unassigned
          work gets the lane it deserves — it is blocking everybody's. */}
      {!appointments.isError && appointments.data && effectiveView === 'week' ? (
        <>
          {wideCalendar ? null : renderAgenda(weekDays, false, true)}
          {wideCalendar ? (
            <div className="schedule-calendar overflow-x-auto">
              <table className="w-full min-w-[52rem] border-collapse" aria-label={t('schedule.page.weekAriaLabel')}>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="w-36 pb-2 pr-2 text-left text-sm font-semibold text-[color:var(--color-muted)]"
                    >
                      {t('schedule.page.weekPersonHeader')}
                    </th>
                    {weekDays.map((date) => (
                      <th
                        key={date}
                        scope="col"
                        className={classes(
                          'pb-2 pl-1 text-left text-sm font-semibold',
                          date === today ? 'text-[color:var(--color-brand)]' : 'text-[color:var(--color-muted)]',
                        )}
                      >
                        {formatShortDate(date)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(showUnassignedLane ? [{ id: 'unassigned', label: t('schedule.page.assigneeUnassigned') }] : [])
                    .concat(laneMembers.map((member) => ({ id: member.id, label: member.fullName })))
                    .map((lane) => (
                      <tr key={lane.id}>
                        <th
                          scope="row"
                          className="border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-2 text-left align-top text-sm font-semibold"
                        >
                          {lane.label}
                        </th>
                        {weekDays.map((date) => (
                          <td
                            key={date}
                            className="h-20 min-w-28 border border-[color:var(--color-line)] p-1 align-top"
                          >
                            {(byLane.get(`${lane.id}|${date}`) ?? []).map(visitChip)}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {!appointments.isError &&
      appointments.data &&
      !(deepLinkAppointmentId && deepLinkedAppointment.isLoading) &&
      effectiveView === 'list' ? (
        listRows.length === 0 ? (
          renderEmptySchedule()
        ) : (
          <ul className="divide-y divide-[color:var(--color-line)]">
            {listRows.map((row) =>
              row.kind === 'external' ? (
                <li key={row.key} className="flex flex-wrap items-center justify-between gap-3 px-2 py-3">
                  <span>
                    <span className="block font-semibold text-[color:var(--color-muted)]">
                      {formatShortDate(row.date)} · {formatLocalTime(row.event.localTime)}
                    </span>
                    <span className="block text-sm text-[color:var(--color-muted)]">{externalLabel(row.event)}</span>
                  </span>
                  <span className="text-sm text-[color:var(--color-muted)]">
                    {row.event.calendarName ?? t('schedule.page.externalSource')}
                  </span>
                </li>
              ) : (
                <li key={row.key}>
                  <button
                    type="button"
                    className={classes(
                      'flex w-full cursor-pointer flex-wrap items-center justify-between gap-3 rounded-lg px-2 py-3 text-left',
                      'hover:bg-[color:var(--color-surface)]',
                      row.appointment.id === selectedId && 'bg-[color:var(--color-surface)]',
                      visitColorClass(row.appointment.colors),
                    )}
                    style={visitColorStyle(row.appointment.colors)}
                    aria-expanded={row.appointment.id === selectedId}
                    onAuxClick={(event) => openCustomerTab(event, row.appointment)}
                    onClick={(event) => {
                      if (openCustomerTab(event, row.appointment)) return;
                      selectAppointment(row.appointment.id === selectedId ? null : row.appointment.id);
                    }}
                  >
                    <span>
                      <span className="block font-semibold">
                        {formatShortDate(row.appointment.scheduledDate)} ·{' '}
                        {formatLocalTime(row.appointment.startTimeLocal)}
                      </span>
                      <span className="block text-sm" style={{ color: 'inherit' }}>
                        {row.appointment.customerName}
                        {row.appointment.propertyAddress ? ` · ${row.appointment.propertyAddress}` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-3">
                      {row.appointment.paymentStatus !== 'not_billed' ? (
                        <PaymentBadge status={row.appointment.paymentStatus} />
                      ) : null}
                      <VisitColorSignals colors={row.appointment.colors} />
                      <AppointmentBadge status={row.appointment.status} />
                      {row.appointment.completionOverride ? (
                        <span className="badge">
                          {t('checklists.overrideFlag', {
                            count: row.appointment.completionOverride.missingRequiredPhotos,
                          })}
                        </span>
                      ) : null}
                      {row.appointment.status === 'completed' &&
                      row.appointment.checklistTotalCount > 0 &&
                      row.appointment.checklistCompletedCount < row.appointment.checklistTotalCount ? (
                        <span className="badge border-[color:var(--color-line)]">
                          {t('schedule.page.checklistIncomplete')}
                        </span>
                      ) : null}
                      {/* Says out loud what the row does. The chevron turns so a
                          row that is already open does not invite another click. */}
                      <span className="whitespace-nowrap font-medium">
                        {row.appointment.id === selectedId ? t('schedule.page.hideManage') : t('schedule.page.manage')}{' '}
                        <span aria-hidden="true">{row.appointment.id === selectedId ? '▾' : '›'}</span>
                      </span>
                    </span>
                  </button>
                  {row.appointment.id === selectedId ? (
                    <div className="mb-3">
                      <VisitManagePanel
                        appointment={row.appointment}
                        assigneeName={crewNames(row.appointment.assignedUserIds, teamById)}
                        onChanged={() => void appointments.refetch()}
                        onClose={() => selectAppointment(null)}
                        startCompleting={startCompletingFromLink && row.appointment.id === deepLinkAppointmentId}
                      />
                    </div>
                  ) : null}
                </li>
              ),
            )}
          </ul>
        )
      ) : null}

      {/*
        The grid has nowhere to open a row, so its panel sits beneath it — and
        beneath six weeks of calendar is off the bottom of a phone. The effect
        above brings it into view and puts the keyboard in it, so opening a
        visit lands you on the visit rather than somewhere further down a page
        you have to go looking through.

        The wrapper keeps its focus ring — no `outline-none` — because the
        global `:focus-visible` rule is what shows a keyboard user where focus
        landed after the scroll, and browsers already withhold it when the
        focus came from a pointer.
      */}
      {selected && effectiveView !== 'list' ? (
        <div ref={monthPanelRef} tabIndex={-1} className="mt-6 scroll-mt-4">
          <VisitManagePanel
            appointment={selected}
            assigneeName={crewNames(selected.assignedUserIds, teamById)}
            onChanged={() => void appointments.refetch()}
            onClose={() => selectAppointment(null)}
            startCompleting={startCompletingFromLink && selected.id === deepLinkAppointmentId}
          />
        </div>
      ) : null}
      <StaffVisitOffersFeed />
    </WorkPage>
  );
}
